import {GapFillingParagraph, GapItem, TextItem} from '../models';
import {ErrorItem, GapFillingCrudParagraph} from './models';

const gapMarker = '[]';
const paragraphSeparator = '\n';

interface GapPositionResult {
  start: number,
  end: number
}

function countGaps(value: string): number {
  let currentPos = 0;
  let result = 0;

  let position = value.indexOf(gapMarker, currentPos);
  while (position !== -1) {
    currentPos = position + gapMarker.length;
    result++;
    position = value.indexOf(gapMarker, currentPos);
  }
  return result;
}

function* findGapPositions(value: string): Generator<GapPositionResult> {
  let currentPos = 0;

  let position = value.indexOf(gapMarker, currentPos);
  while (position !== -1) {
    yield {start: position, end: position + gapMarker.length};
    currentPos = position + gapMarker.length;
    position = value.indexOf(gapMarker, currentPos);
  }
}

function deserializeParagraph(
  line: string,
  items: (GapItem | ErrorItem)[]): (GapItem | TextItem | ErrorItem)[] {
  let result: (GapItem | TextItem | ErrorItem)[] = [];
  let lastPosition = 0;

  function addTextItem(start: number, end: number) {
    if (start === end)
      return;
    const content = line.substring(start, end);
    let item: TextItem = {
      type: 'text',
      content: content
    };
    result.push(item);
  }

  function addGapItem() {
    const item = items.pop();
    if (item === undefined)
      result.push({type: 'gap-error', message: 'value not specified'});
    else
      result.push(item);
  }

  for (let gapPosition of findGapPositions(line)) {
    addTextItem(lastPosition, gapPosition.start);
    addGapItem();
    lastPosition = gapPosition.end;
  }

  addTextItem(lastPosition, line.length);
  return result;
}

function splitLines(value: string): string[] {
  function notEmptyLine(line: string): boolean {
    line = line.trim();
    return (line !== '');
  }

  return value.split(paragraphSeparator).filter(notEmptyLine);
}

function deserializeParagraphs(value: string, items: (GapItem | ErrorItem)[]): GapFillingCrudParagraph[] {
  // reversed, because we are going to pop gap items.
  items.reverse();
  let result: GapFillingCrudParagraph[] = [];
  for (let line of splitLines(value)) {
    let paragraph = deserializeParagraph(line, items);
    result.push({items: paragraph});
  }
  return result;
}

function serializeParagraph(paragraph: GapFillingParagraph): string {
  let result: string[] = [];

  function addText(item: TextItem) {
    result.push(item.content);
  }

  function addGap() {
    result.push(gapMarker);
  }

  for (let item of paragraph.items) {
    if (item.type === 'text')
      addText(item);
    if (item.type === 'gap')
      addGap();
  }
  return result.join('');
}

function serializeParagraphs(paragraphs: readonly GapFillingParagraph[]): string {
  return paragraphs.map((p) => serializeParagraph(p))
    .join(paragraphSeparator);
}

function gapItemsList(paragraphs: readonly GapFillingParagraph[]): GapItem[] {
  const result: GapItem[] = [];
  for (let paragraph of paragraphs)
    for (let item of paragraph.items)
      if (item.type === 'gap')
        result.push(item);
  return result;
}

/******************************************************************************/

class GapFillingCrudHelperClass {
  readonly paragraphSeparator = paragraphSeparator;

  readonly countGaps = countGaps;
  readonly deserializeParagraphs = deserializeParagraphs;

  readonly serializeParagraphs = serializeParagraphs;

  readonly gapItemsList = gapItemsList;
}

export const GapFillingCrudHelper = new GapFillingCrudHelperClass();
